import {
  Component, ViewEncapsulation, ChangeDetectionStrategy,
  Input, OnInit, ViewChild, ElementRef, Output, EventEmitter, ChangeDetectorRef, OnChanges, SimpleChanges
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { OptionSelected } from '@booking/entities/booking-arrangement';
import { TravelerTypes, TravelerInfoType } from '@pax/entities/traveler-info';
import { BillingPersonType } from '@booking/entities/billing-person-types';
import { PERSON_ADDRESS_FORM_INPUTS, PERSON_FORM_INPUTS, TRAVELER_FORM_INPUTS } from '@booking/constants/forms.constants';
import { FormInputType, FormDocumentInputTemplate } from '@forms/form-text-input/entities/input-type';
import { DropdownSelectComponent } from '@ux/components/dropdown-select/dropdown-select.component';
import { FormValidationService } from '@reservation/services/form-validation.service';
import { FormInputsService } from '@reservation/services/form-inputs.service';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import {
  BookingBillingTypeDocumentProps, BookingBillingDocumentValidations,
  BookingDocumentError, BookingPaxTypeDocumentProps, BookingPaxDocumentValidations
} from '@app/entities/booking-documents';
import {
  documentInputPlaceholder, getDocumentCustomErrors, getPaxDocumentIdByName
} from '@forms/mappers/form-document-input.mapper';
import { BookingDocumentPaxIdType } from '@app/entities/booking-pax-documents-id';
import { BookingDocumentBillingIdType } from '@app/entities/booking-billing-documents-id';
import { FORM_COMPONENTS_PROPS } from '@forms/form-components/constants/form-components-props.constants';

@Component({
  selector: 'app-form-document-input',
  templateUrl: './form-document-input.component.html',
  styleUrls: ['./form-document-input.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FormDocumentInputComponent implements OnInit, OnChanges {

  @Input() form: UntypedFormGroup;
  @Input() formType: TravelerInfoType = TravelerInfoType.PASSENGER;
  @Input() type: TravelerTypes | BillingPersonType;
  @Input() documents: BookingPaxTypeDocumentProps[] | BookingBillingTypeDocumentProps[] = [];
  @Input() isInternationalTrip: boolean;
  @Input() isSubmitted: boolean;
  @Input() validators: BookingPaxDocumentValidations | BookingBillingDocumentValidations;
  @Input() documentType: BookingDocumentPaxIdType | BookingDocumentBillingIdType;
  @Input() travelerBookingProvider: TravelBookingType;
  @Input() documentNumberSelector: string = TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER;
  @Input() configurationDocumentId: string = TRAVELER_FORM_INPUTS.DOCUMENT_ID;
  @Input() defaultDocument: string;
  @Input() isMobile: boolean;

  @ViewChild('paxInput') public paxInputRef: ElementRef;
  @ViewChild('billingInput') public billingInputRef: ElementRef;

  @ViewChild('typeSelector', { read: DropdownSelectComponent }) public typeSelector: DropdownSelectComponent;

  @Output() documentSelected: EventEmitter<OptionSelected> = new EventEmitter<OptionSelected>();

  public documentInput: FormInputType = FormInputType.DOCUMENT;

  constructor(
    private formInputsService: FormInputsService,
    private formValidationService: FormValidationService,
    private translate: TranslocoService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.[FORM_COMPONENTS_PROPS.DEFAULT_DOCUMENT]) {
      if (this.typeSelector) this.typeSelector.defaultOption = this.defaultDocument;
      this.changeDetector.detectChanges();
    }
  }

  ngOnInit(): void {
    this.onDocumentTypeSelected({ value: this.defaultDocument, index: null });
  }

  get formDocumentInputTemplate(): typeof FormDocumentInputTemplate {
    return FormDocumentInputTemplate;
  }

  get paxValidators(): BookingPaxDocumentValidations {
    return this.validators as BookingPaxDocumentValidations;
  }

  get billingValidators(): BookingBillingDocumentValidations {
    return this.validators as BookingBillingDocumentValidations;
  }

  get isPaxValues(): boolean {
    return Object.values(TravelerTypes)?.includes(this.type as TravelerTypes);
  }

  get isBillingValues(): boolean {
    return Object.values(BillingPersonType)?.includes(this.type as BillingPersonType);
  }

  get countryCode(): string {
    return this.type === BillingPersonType.JURIDICAL_PERSON || this.type === BillingPersonType.NATURAL_PERSON ?
      this.form?.get(PERSON_FORM_INPUTS.ADDRESS)?.get(PERSON_ADDRESS_FORM_INPUTS.COUNTRY_CODE)?.value :
      this.form?.get(TRAVELER_FORM_INPUTS.NATIONALITY)?.value;
  }

  get documentTags(): string[] {
    return [...this.documents].map(document => document.DOCUMENT_NAME);
  }

  get documentCustomErrors(): BookingDocumentError[] {
    return getDocumentCustomErrors(this.type, this.documentType, this.documents);
  }

  get oneDocument(): boolean {
    return this.documentTags.length === 1;
  }

  get hiddenPlaceholder(): boolean {
    return this.oneDocument || (this.isBillingValues &&
      this.documentType as BookingDocumentBillingIdType === BookingDocumentBillingIdType.NIT);
  }

  get label(): string {
    return this.oneDocument ? this.documentTags[0] : this.getTranslate('BOOKING_MODULE.DOCUMENT_NUMBER') as string;
  }

  get placeholderNumber(): string {
    return documentInputPlaceholder(this.type, this.documentType, this.oneDocument);
  }

  get marginStyle(): string {
    return !this.oneDocument ? 'margin' : '';
  }

  get internationalDocumentRequired(): boolean {
    return !!this.form && !!this.form?.get(TRAVELER_FORM_INPUTS.INTERNATIONAL_DOCUMENT_REQUIRED);
  }

  get showInternationalDocumentError(): boolean {
    return this.internationalDocumentRequired && this.isSubmitted && !this.internationalDocumentFilledOut;
  }

  get internationalDocumentFilledOut(): boolean {
    return this.internationalDocumentRequired && this.form?.get(TRAVELER_FORM_INPUTS.INTERNATIONAL_DOCUMENT_REQUIRED)?.valid;
  }

  get documentError(): boolean {
    return this.internationalDocumentRequired ? this.isFieldInvalid(this.configurationDocumentId) ||
      this.showInternationalDocumentError : this.isFieldInvalid(this.configurationDocumentId);
  }

  public scroll(elementRef: ElementRef): void {
    this.formInputsService.scrollIntoView(elementRef);
    this.changeDetector.detectChanges();
  }

  public addErrorClass(field: string): { [key: string]: boolean } {
    return this.formInputsService.addErrorClass(field, this.form, this.isSubmitted);
  }

  public onDocumentTypeSelected(event: OptionSelected): void {
    if (this.type) {
      const value = getPaxDocumentIdByName(event.value as string, this.type, this.documents);
      this.documentSelected.emit({ value, index: event.index });
      if (this.isPaxValues) {
        this.formValidationService.setPaxDocumentValidators(
          this.documentType as BookingDocumentPaxIdType, this.form, this.paxValidators);
      } else {
        this.formValidationService.setBillingDocumentValidators(this.form,
          this.documentType as BookingDocumentBillingIdType, this.billingValidators);
      }
      this.applyOrRemoveMask();
    }
  }

  public changeByCountry(
    documentInForm: string, defaultDocument: string, tags: string[] = []
  ): void {
    const isOnlyOne = tags.length ? tags.length === 1 : this.oneDocument;
    const tagsValid = tags.length ? tags : this.documentTags;
    if (isOnlyOne) {
      this.defaultDocument = defaultDocument;
    } else {
      if (tagsValid.includes(documentInForm)) {
        this.defaultDocument = documentInForm;
      } else this.defaultDocument = null;
    }
    if (!this.oneDocument) this.onDocumentTypeSelected({ value: this.defaultDocument, index: null });
  }

  public isFieldInvalid(field: string): boolean {
    return this.formInputsService.isFieldInvalid(field, this.form, this.isSubmitted);
  }

  private getTranslate(key: string | string[]): string | string[] {
    return this.translate.translate(key);
  }

  public applyOrRemoveMask(): void {
    const documentNumber = this.form?.get(this.documentNumberSelector)?.value;
    const mask = this.isPaxValues ? this.paxValidators?.MASK : this.isBillingValues ? this.billingValidators?.MASK : null;
    this.formValidationService.applyOrRemoveDocumentMask(
      this.form, documentNumber, this.documentNumberSelector, mask
    );
  }

}
